import React, { useEffect } from 'react'
import Meta from 'components/modules/SEO/Meta'
import { GetStaticPaths, GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import PrismicService from 'services/PrismicService'
import { LandingPagePrismicDocument } from 'interfaces/PrismicDocument'
import {
    snapchatMarketingPixels,
    returnSliceComponent,
    sessionDurationTrackingComponent,
    linkedInMarketingPixels,
    facebookMessengerWidget,
} from 'lib/features/landingPageGeneration'
import { onPageEnter } from 'lib/tracking/pageEnter'
import { getHashFromUrl, getReferrerScreen } from 'lib/formatting/url'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AnalyticsEventLandingPagePageView } from 'lib/constants/AnalyticsEventType'
import { getLocales, parseLocale } from 'lib/constants/localization'
import { trackSnapchatEvent, SnapchatEvent } from 'lib/tracking/snapchat'
import { trackTikTokEvent, TikTokEventType } from 'lib/tracking/tiktok'
import { useRouter } from 'next/router'

interface Props {
    prismicDocument: LandingPagePrismicDocument
    slug: string
}

const GeneratedLandingPage = (props: Props) => {
    const router = useRouter()
    const referrerScreen = getReferrerScreen(router)

    const showSnapchatPixel = props.prismicDocument.data.enable_snapchat_pixel
    useEffect(() => {
        onPageEnter()
        trackEvent(AnalyticsEventLandingPagePageView, {
            value: props.slug,
            referrerScreen,
        })
        trackTikTokEvent(TikTokEventType.ViewContent)
        showSnapchatPixel && trackSnapchatEvent(SnapchatEvent.PageView)
    }, [props.slug, referrerScreen, showSnapchatPixel])

    useEffect(() => {
        const hash = getHashFromUrl()
        if (!hash) return
        const body = document.querySelector(hash)
        if (!body) return

        setTimeout(
            () =>
                body.scrollIntoView({
                    behavior: 'smooth',
                }),
            2000
        )
    }, [])

    return (
        <>
            <Meta
                title={props.prismicDocument.data.page_title}
                description={props.prismicDocument.data.page_description}
                noIndex={!!props.prismicDocument.data.noindex}
            />
            {props.prismicDocument.data && props.prismicDocument.data.body.map((item) => returnSliceComponent(item))}
            {linkedInMarketingPixels(props.prismicDocument)}
            {snapchatMarketingPixels(props.prismicDocument)}
            {facebookMessengerWidget(props.prismicDocument)}
            {sessionDurationTrackingComponent(props.prismicDocument)}
        </>
    )
}

export const getStaticPaths: GetStaticPaths = async (props) => {
    let paths: { params: { slug: string }; locale: string }[] = []

    for (const locale of Object.keys(getLocales())) {
        const parsedLocale = parseLocale(locale)
        const res = await PrismicService.listLandingPages(parsedLocale.prismicLanguage)
        const prismicPaths = res ? res.results.map((document) => document.uid) : []
        const filteredPrismicPaths = prismicPaths.filter((path): path is string => !!path)
        const structuredPaths = filteredPrismicPaths.map((name) => ({
            params: {
                slug: name,
            },
            locale: locale,
        }))
        paths = [...paths, ...structuredPaths]
    }

    return {
        paths,
        fallback: false,
    }
}

export const getStaticProps: GetStaticProps<Props> = async (props) => {
    const slug = props.params?.slug
    if (!(slug && typeof slug === 'string')) {
        return { notFound: true }
    }

    const parsedLocale = parseLocale(props.locale)
    const res = await PrismicService.getLandingPage(slug, parsedLocale.prismicLanguage)

    if (!res) {
        return { notFound: true }
    }
    if (!res.data.islive) {
        return { notFound: true }
    }

    return {
        props: {
            prismicDocument: { data: res.data },
            slug,
            ...(await serverSideTranslations(parseLocale(props.locale).interfaceLanguageCode, ['common'])),
        },
    }
}

export default GeneratedLandingPage
